import {
  useBoardMeetings,
  useBoardPackBuilder,
  useCreateBoardMemo,
} from "@l30/web-board/api/board-meetings";

import { useCurrentRole, useCurrentUserEmail, useIsAdmin } from "../api/auth.js";
import {
  useDefaultEngagement,
  usePortalBrief,
  usePublishPortalBrief,
  useSubmitPortalBriefForReview,
  useUpdatePortalBrief,
} from "../api/engagement-plan.js";
import { useChannels, useSendMessage } from "../api/messages.js";
import { useCreateNote } from "../api/notes.js";
import {
  useCreateProject,
  useCreateProjectDiscoveryBrief,
  useCreateTask,
  useImportMapMilestones,
  useProjectByMapId,
  useProjectTasks,
  useProjects,
  useProjectWorkspace,
} from "../api/projects.js";
import { useTenants } from "../api/tenants.js";
import { useCreateWorkspaceDocument, useUploadWorkspaceVersion } from "../api/workspaces.js";
import { useHelp } from "../components/help/help-context.js";
import { NextActions } from "../components/help/next-actions.js";
import { PanelHelpButton } from "../components/help/panel-help-button.js";
import { queryClient } from "../lib/query-client.js";

import type { AtlasHubServices } from "@l30/web-atlas/host";

/**
 * Hub-side implementation of the `AtlasHubServices` port (ADR-0101 Phase 3).
 *
 * `@l30/web-atlas` must not import `packages/web/src/**`, so every
 * cross-domain hook/handoff it needs is proxied through this factory and
 * injected via `<AtlasHubProvider>` (mounted in `_authenticated.tsx`). This
 * is a plain object of hook/function references — not a hook itself — so it
 * can be constructed once per render without violating rules-of-hooks.
 */
export function createAtlasHubServices(): AtlasHubServices {
  return {
    useCurrentRole,
    useIsAdmin,
    useCurrentUserEmail,

    queryClient,

    useHelp,
    NextActions,
    PanelHelpButton,

    useBoardMeetings,
    useBoardPackBuilder,
    useCreateBoardMemo,

    useCreateProject,
    useCreateProjectDiscoveryBrief,
    useCreateTask,
    useImportMapMilestones,
    useProjectByMapId,
    useProjectTasks,
    useProjects,
    useProjectWorkspace,

    useCreateWorkspaceDocument,
    useUploadWorkspaceVersion,

    useDefaultEngagement,
    usePortalBrief,
    useUpdatePortalBrief,
    useSubmitPortalBriefForReview,
    usePublishPortalBrief,

    useTenants,

    useChannels,
    useSendMessage,

    useCreateNote,
  };
}
